import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { EmailType } from '@acp/shared';
import { SendEmailModalRef } from '../../../components/forms';
import { SendEmailModal } from '../../../components/modals';
import { ForwardEmailFormValues } from '../../email';
import { sendNotificationAndResolveActiveNotice } from '../service';

export function EmailTenantModal({
    isOpen,
    onClose,
    propertyId,
    noticeId,
}: {
    isOpen: boolean;
    onClose: () => void;
    propertyId: number;
    noticeId: number;
}) {
    const queryClient = useQueryClient();

    const formRef = useRef<SendEmailModalRef>(null);

    const toast = useToast();

    const sendEmailMutation = useMutation({
        mutationFn: async ({
            toAddresses,
            ccAddresses,
            message,
        }: {
            toAddresses: string[];
            ccAddresses: string[];
            message: string;
        }) => {
            await sendNotificationAndResolveActiveNotice(
                noticeId,
                propertyId,
                toAddresses,
                ccAddresses,
                message
            );
        },
        onSuccess: () => {
            toast({
                title: 'Email Sent',
                description: 'The specified recipient(s) should receive the email shortly.',
                status: 'success',
                isClosable: true,
            });

            void queryClient.invalidateQueries({
                queryKey: ['notices'],
            });

            formRef.current?.resetForm();
            onClose();
        },
        onError: (error) => {
            toast({
                title: 'Failed to send notification and resolve access issue',
                description: error.message,
                status: 'error',
                isClosable: true,
            });
        },
    });

    const handleClose = () => {
        formRef.current?.resetForm();
        sendEmailMutation.reset();
        onClose();
    };

    const onSubmit = (vals: ForwardEmailFormValues) => {
        sendEmailMutation.mutate({
            toAddresses: vals.toEmails.map((email) => email.value),
            ccAddresses: vals.ccEmails.map((email) => email.value),
            message: vals.emailMessage,
        });
    };

    return (
        <SendEmailModal
            ref={formRef}
            title="Send email to tenant"
            extraErrorMessage="Failed to send notification and resolve access issue"
            propertyId={propertyId}
            mutation={sendEmailMutation}
            onClose={handleClose}
            onSubmit={onSubmit}
            isOpen={isOpen}
            includePropertyManagers
            includeOccupants
            previewAvailable
            metaData={{
                emailType: EmailType.OCCUPANT_NOTICE,
                noticeId,
                propertyId,
            }}
        />
    );
}

export function SendNotificationModalButton({
    propertyId,
    noticeId,
}: {
    propertyId: number;
    noticeId: number;
}) {
    const disclosure = useDisclosure();

    return (
        <>
            <MenuItem onClick={disclosure.onOpen}>Send email to tenant</MenuItem>
            <EmailTenantModal
                propertyId={propertyId}
                noticeId={noticeId}
                onClose={disclosure.onClose}
                isOpen={disclosure.isOpen}
            />
        </>
    );
}
